// import './polyfills';
import { enableProdMode, provideZoneChangeDetection, ErrorHandler, importProvidersFrom } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { provideRouter, withRouterConfig } from '@angular/router';
import { DatePipe } from '@angular/common';
import { HIGHLIGHT_OPTIONS } from 'ngx-highlightjs';

import { environment } from './environments/environment';
import * as Sentry from '@sentry/angular';
import { UtilService } from './app/doc-process/sub-modules/doc-process-common/services/util.service';
import 'codemirror/mode/javascript/javascript';
import 'codemirror/addon/fold/foldgutter';
import 'codemirror/addon/fold/brace-fold';
import 'codemirror/lib/codemirror';
import 'codemirror/addon/edit/closebrackets';
import 'codemirror/addon/edit/matchbrackets';
import 'codemirror/addon/lint/lint';

import 'quill-mention/autoregister';
import Quill from 'quill';
import { QuillModule } from 'ngx-quill';
import moment from 'moment';
import { FontStyleBlot, RawHtmlBlot } from 'app/shared/utilities.classes';
import { LayoutService } from './app/layout.service';
import { provideBootstrapConfig } from './app/utility/bootstrap-config.class';
import { HTTP_INTERCEPTORS, provideHttpClient, withXhr, withInterceptorsFromDi } from '@angular/common/http';
import { RefreshJwtInterceptor } from './app/auth/services/refresh-jwt.service';
import { Router } from '@angular/router';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { JwtModule } from '@auth0/angular-jwt';
import { AuthSettings } from './app/settings.class';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { DragulaModule } from 'ng2-dragula';
import { appRoutes } from './app/app.routes';
import { MomentModule } from 'ngx-moment';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { IMaskModule } from 'angular-imask';
import { MarkdownModule } from 'ngx-markdown';
import { NgxsModule } from '@ngxs/store';
import { TaskDataEntryState } from './app/doc-process/sub-modules/universal-data-entry/state-management/states';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { AppComponent } from './app/app.component';

if (environment.production || environment.oldstage) {
  enableProdMode();
}

const align = Quill.import('attributors/style/align') as any;
const direction = Quill.import('attributors/style/direction') as any;
const size = Quill.import('attributors/style/size') as any;
const background = Quill.import('attributors/style/background') as any;
const color = Quill.import('attributors/style/color') as any;
const indent = Quill.import('formats/indent') as any;
const font: any = Quill.import('formats/font');
font.whitelist = ['consolas', 'serif', 'sans-serif', 'monospace'];

Quill.register(font, true);
Quill.register(align, true);
Quill.register(direction, true);
Quill.register(size, true);
Quill.register(background, true);
Quill.register(color, true);
Quill.register(indent, true);
Quill.register(FontStyleBlot, true);
Quill.register(RawHtmlBlot, true);

moment.updateLocale('en', {
  longDateFormat: {
    LT: 'hh:mm',
    LTS: 'hh:mm:ss',
    L: 'yyyy-MM-DD',
    l: 'yyyy-MM-DD',
    LL: 'MMMM Do yyyy',
    ll: 'MMM D yyyy',
    LLL: 'MMMM Do yyyy LT',
    lll: 'MMM D yyyy LT',
    LLLL: 'dddd, MMMM Do yyyy LT',
    llll: 'ddd, MMM D yyyy LT',
  },
});

let tracingOrigins = [
  'localhost',
  'connector.test',
  'infront',
  'inquiry',
  'connector',
  'infront-doc-processing-dev',
  'infront-doc-processing-test',
  'infront-doc-processing-labeling',
  'infront-doc-processing-prod3',
];

Sentry.init({
  dsn: 'https://076bc31137bb4e739a17b15de24ce76c@o1167486.ingest.sentry.io/6258596',
  tunnel: environment.api_host + '/api/sentry/request-event',
  tracesSampleRate: 0.05, // environment.production === true' ? 0.2 : 1,
  tracePropagationTargets: tracingOrigins, // Move tracingOrigins to top-level config
  integrations: [Sentry.browserTracingIntegration()],
  beforeSend(event) {
    event.breadcrumbs?.map((bc) => {
      if (bc.category !== 'xhr') return bc;

      if (bc.data?.url) {
        bc.data.url = UtilService.removeURLParameter(bc.data.url, 'code');
        bc.data.url = UtilService.removeURLParameter(bc.data.url, 'userToken');
      }
      return bc;
    });
    return event;
  },
});

Sentry.getCurrentScope().setLevel('warning');

function setupSentryContext() {
  try {
    const storedUser = JSON.parse(localStorage.getItem('USER-DETAILS') || '{}');
    const strippedUser = {
      email: storedUser?.email ?? '',
      roles: storedUser?.roles ?? '',
      created_at: storedUser?.created_at ?? '',
    };
    const localStorageValue = Object.assign({}, localStorage);
    delete localStorageValue.token;

    Sentry.setUser(strippedUser);
    Sentry.setExtras(localStorageValue);
    Sentry.setTag('dp-backend-env', localStorageValue?.['doc_process_server']);
    Sentry.setTag('user-email', localStorageValue?.['email']);
    Sentry.setTag('hostname', window.location.hostname);
  } catch (error) {
    console.warn('Failed to setup Sentry context:', error);
  }
}

setupSentryContext();

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    importProvidersFrom(
      BrowserModule,
      FormsModule,
      JwtModule.forRoot({
        config: {
          tokenGetter: AuthSettings.getToken,
          allowedDomains: AuthSettings.WHITELISTED_DOMAINS,
          disallowedRoutes: AuthSettings.BLACKLISTED_ROUTES,
          headerName: AuthSettings.HEADER_NAME,
          authScheme: AuthSettings.HEADER_PREFIX,
        },
      }),
      QuillModule.forRoot(),
      NgbModule,
      DragulaModule.forRoot(),
      MomentModule,
      MarkdownModule.forRoot(),
      BrowserAnimationsModule,
      ToastrModule.forRoot({
        timeOut: 2500,
        positionClass: 'toast-bottom-right',
        preventDuplicates: true,
        closeButton: true,
      }),
      IMaskModule,
      NgxsModule.forRoot([TaskDataEntryState]),
      NgxsReduxDevtoolsPluginModule.forRoot()
    ),
    provideRouter(appRoutes, withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' })),
    LayoutService,
    DatePipe,
    {
      provide: HIGHLIGHT_OPTIONS,
      useValue: {
        coreLibraryLoader: () => import('highlight.js/lib/core'),
        languages: {
          json: () => import('highlight.js/lib/languages/json'),
        },
      },
    },
    provideBootstrapConfig(),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RefreshJwtInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
  ],
});
